import React, { useEffect, useMemo, useState } from 'react';
import { ChartBar, ChartLine } from '@phosphor-icons/react';
import { fetchJourneyFilters } from '../../api/journeys';
import { fetchRevenueMovement } from '../../api/phase2Analytics';
import { PanelStateMessage } from '../../components/dashboard/PanelStateMessage';
import { PageSummaryStats } from '../../components/dashboard/PageSummaryStats';
import { RevenueMovementChart } from '../../components/dashboard/RevenueMovementChart';
import { ConfidenceStrip } from '../../components/journeys/ConfidenceStrip';
import { JourneyFilterBar } from '../../components/journeys/JourneyFilterBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { DISPLAY_CURRENCY_OPTIONS, useCurrency } from '../../context/CurrencyContext';
import { useTenantView } from '../../context/TenantViewContext';
import { JOURNEY_FILTER_OPTIONS } from '../../data/journeysMock';
import { usePersistedState } from '../../hooks/usePersistedState';
import { hasExchangeRate } from '../../lib/convertDashboardData';
import { formatMoney, formatNumber } from '../../lib/formatMoney';
import { buildJourneyApiFilters } from '../../lib/journeyApiFilters';
import { mapRevenueMovementToView } from '../../lib/mapPhase2Analytics';
import { cn } from '../../lib/utils';

const DEFAULT_FILTERS = {
  season: 'all',
  venue: 'all',
  event: 'all',
  currency: 'DKK',
};

const PREFS_KEY = 'vi.revenueMovement.prefs.v1';
const CHART_PREFS_KEY = 'vi.revenueMovement.chartType.v1';
const ALL_VENUE = { value: 'all', label: 'Venue: all' };
const ALL_EVENT = { value: 'all', label: 'Event: all' };
const SOURCE_CURRENCY = 'DKK';

function mapFilterOptions(items, kind) {
  return (items || []).map((item) => ({
    value: String(item.value),
    label: kind === 'venue' ? `Venue: ${item.label}` : `Event: ${item.label}`,
  }));
}

function convertAmount(value, rate) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return 0;
  return amount * rate;
}

function downloadCsv(view, currencyCode) {
  const header = `date,cmp,pos,app,total,currency\n`;
  const rows = (view.series || [])
    .map((row) => {
      const total = Number(row.cmp || 0) + Number(row.pos || 0) + Number(row.app || 0);
      return `${row.date},${row.cmp},${row.pos},${row.app},${total},${currencyCode}`;
    })
    .join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `revenue-movement-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function RevenueMovementPage() {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const { rates, ratesError } = useCurrency();
  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;

  const [storedFilters, setStoredFilters] = usePersistedState(PREFS_KEY, DEFAULT_FILTERS);
  const appliedFilters = { ...DEFAULT_FILTERS, ...storedFilters };
  const [draftFilters, setDraftFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    ...storedFilters,
  }));
  const [chartType, setChartType] = usePersistedState(CHART_PREFS_KEY, 'stacked');
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [venueOptions, setVenueOptions] = useState([ALL_VENUE, ...JOURNEY_FILTER_OPTIONS.venues.slice(1)]);
  const [eventOptions, setEventOptions] = useState([ALL_EVENT, ...JOURNEY_FILTER_OPTIONS.events.slice(1)]);

  const displayCurrency = appliedFilters.currency || SOURCE_CURRENCY;
  const ratesReady = hasExchangeRate(rates, displayCurrency, SOURCE_CURRENCY);
  const currencyUnavailable = displayCurrency !== SOURCE_CURRENCY && (!ratesReady || Boolean(ratesError));
  const conversionRate =
    displayCurrency === SOURCE_CURRENCY || !ratesReady ? 1 : Number(rates[displayCurrency]);

  const filterFields = useMemo(
    () => [
      {
        key: 'currency',
        label: 'Currency',
        options: DISPLAY_CURRENCY_OPTIONS.map((opt) => ({
          value: opt.value,
          label: ratesError && opt.value !== SOURCE_CURRENCY ? `${opt.label} (rates unavailable)` : opt.label,
        })),
      },
      {
        key: 'season',
        label: 'Season',
        options: [
          { value: 'all', label: 'All seeded dates' },
          ...JOURNEY_FILTER_OPTIONS.seasons,
        ],
      },
      { key: 'venue', label: 'Venue', options: venueOptions },
      { key: 'event', label: 'Event', options: eventOptions },
    ],
    [venueOptions, eventOptions, ratesError]
  );

  function handleFilterChange(key, value) {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
    // Currency applies immediately, matching other money screens.
    if (key === 'currency') {
      setStoredFilters((prev) => ({ ...DEFAULT_FILTERS, ...prev, currency: value }));
    }
  }

  function handleApply() {
    setStoredFilters(draftFilters);
  }

  useEffect(() => {
    let cancelled = false;
    async function loadFilters() {
      try {
        const data = await fetchJourneyFilters({ accountId });
        if (cancelled) return;
        setVenueOptions([ALL_VENUE, ...mapFilterOptions(data.venues, 'venue')]);
        setEventOptions([ALL_EVENT, ...mapFilterOptions(data.events, 'event')]);
      } catch {
        // Keep mock options.
      }
    }
    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const filters = buildJourneyApiFilters({
          season: appliedFilters.season,
          venue: appliedFilters.venue,
          event: appliedFilters.event,
          accountId,
        });
        const { data, source } = await fetchRevenueMovement(filters);
        if (cancelled) return;
        setView(mapRevenueMovementToView(data, source));
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Unable to load revenue movement.');
        setView(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [appliedFilters.season, appliedFilters.venue, appliedFilters.event, accountId]);

  const displayView = useMemo(() => {
    if (!view) return null;
    if (currencyUnavailable || conversionRate === 1) {
      return {
        ...view,
        currencyCode: currencyUnavailable ? SOURCE_CURRENCY : displayCurrency,
      };
    }
    return {
      ...view,
      currencyCode: displayCurrency,
      totals: {
        cmp: convertAmount(view.totals.cmp, conversionRate),
        pos: convertAmount(view.totals.pos, conversionRate),
        app: convertAmount(view.totals.app, conversionRate),
      },
      series: view.series.map((row) => ({
        ...row,
        cmp: convertAmount(row.cmp, conversionRate),
        pos: convertAmount(row.pos, conversionRate),
        app: convertAmount(row.app, conversionRate),
      })),
    };
  }, [view, displayCurrency, conversionRate, currencyUnavailable]);

  const totals = displayView?.totals || { cmp: 0, pos: 0, app: 0 };
  const grandTotal = Number(totals.cmp) + Number(totals.pos) + Number(totals.app);
  const moneyCurrency = currencyUnavailable ? SOURCE_CURRENCY : displayCurrency;

  const summaryStats = useMemo(() => {
    if (!displayView || displayView.empty) return [];
    const money = (amount) => formatMoney(amount, { currency: moneyCurrency });
    const share = (amount) =>
      grandTotal > 0
        ? `${formatNumber((Number(amount) / grandTotal) * 100, { decimals: 0 })}% of total`
        : 'Share of total';

    return [
      {
        title: 'Total',
        value: money(grandTotal),
        change: '—',
        trend: 'neutral',
        context: 'All sources in window',
        featured: true,
      },
      {
        title: 'CMP',
        value: money(totals.cmp),
        change: '—',
        trend: 'neutral',
        context: share(totals.cmp),
      },
      {
        title: 'POS',
        value: money(totals.pos),
        change: '—',
        trend: 'neutral',
        context: share(totals.pos),
      },
      {
        title: 'APP',
        value: money(totals.app),
        change: '—',
        trend: 'neutral',
        context: share(totals.app),
      },
    ];
  }, [displayView, grandTotal, totals.cmp, totals.pos, totals.app, moneyCurrency]);

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      <JourneyFilterBar
        fields={filterFields}
        values={draftFilters}
        onChange={handleFilterChange}
        onApply={handleApply}
        onExport={
          displayView && !displayView.empty
            ? () => downloadCsv(displayView, moneyCurrency)
            : undefined
        }
        exportLabel="Export CSV"
        dataSourceLabel={displayView?.dataSourceLabel}
      />

      {currencyUnavailable ? (
        <ConfidenceStrip
          tone="warning"
          title="Exchange rates unavailable — amounts shown in DKK."
        />
      ) : null}

      {displayView?.notes?.[0] ? (
        <ConfidenceStrip
          tone={displayView.empty ? 'warning' : 'success'}
          title={
            displayView.empty
              ? 'No source-split revenue for this window yet — POS/APP stay empty until those pipelines flow.'
              : displayView.notes[0]
          }
        />
      ) : null}

      {loading ? (
        <PanelStateMessage
          title="Loading revenue movement"
          message="Pulling CMP, POS, and APP totals for the selected window."
        />
      ) : error ? (
        <PanelStateMessage title="Couldn't load revenue movement" message={error} variant="error" />
      ) : !displayView || displayView.empty ? (
        <PanelStateMessage
          title="No source-split revenue"
          message="Try another season or venue. POS and APP often show zero until lake ingest catches up."
        />
      ) : (
        <>
          <PageSummaryStats stats={summaryStats} isFirstLoad={false} />

          <Card>
            <CardHeader className="px-4 py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1 text-left">
                  <CardTitle>Source split over time</CardTitle>
                  <CardDescription>
                    {chartType === 'lines'
                      ? 'CMP, POS, and APP trends — zoom in for daily detail.'
                      : 'Stacked CMP, POS, and APP — zoom in for daily detail.'}
                  </CardDescription>
                </div>
                <div
                  className="inline-flex shrink-0 self-end rounded-lg border border-slate-200 bg-[#F7F9FC] p-0.5 sm:self-start"
                  role="group"
                  aria-label="Chart type"
                >
                  <button
                    type="button"
                    onClick={() => setChartType('stacked')}
                    className={cn(
                      'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-semibold transition',
                      chartType === 'stacked'
                        ? 'bg-white text-[#201C35] shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                    aria-pressed={chartType === 'stacked'}
                  >
                    <ChartBar size={14} weight="bold" aria-hidden="true" />
                    Stacked
                  </button>
                  <button
                    type="button"
                    onClick={() => setChartType('lines')}
                    className={cn(
                      'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-semibold transition',
                      chartType === 'lines'
                        ? 'bg-white text-[#201C35] shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                    aria-pressed={chartType === 'lines'}
                  >
                    <ChartLine size={14} weight="bold" aria-hidden="true" />
                    Lines
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <RevenueMovementChart
                series={displayView.series}
                currencyCode={moneyCurrency}
                chartType={chartType}
              />
            </CardContent>
          </Card>
        </>
      )}
    </section>
  );
}
